import { hasAnyRole } from '@berky/auth'
import type { ModuleConfig } from '@berky/module-contract'

/**
 * Was ein Nutzer mit einem Modul darf.
 *
 * `verborgen` heißt heute: nicht in der Navigation, und der direkte Aufruf der
 * Route endet auf `/forbidden`.
 *
 * `unbekannt` ist kein Ergebnis, sondern das Fehlen eines Ergebnisses: Die
 * Rollen sind noch nicht da. Das ist ausdrücklich **nicht** dasselbe wie
 * `verborgen` — wer das gleichsetzt, schickt jeden Direktlink auf `/forbidden`,
 * bevor die Anmeldung überhaupt durch ist. Genau das ist hier schon einmal
 * passiert; deshalb steht der Zustand jetzt im Typ und lässt sich nicht mehr
 * versehentlich übersehen.
 *
 * Absichtlich eine Aufzählung und kein `boolean`: Vorgesehen ist ein weiterer
 * Zustand `beantragbar` — Modul sichtbar, Zugang auf Anfrage. Damit das später
 * eine Ergänzung und kein Umbau wird, treffen Seitenleiste und Route ihre
 * Entscheidung schon heute an genau dieser einen Stelle.
 */
export type ModuleAccess = 'erlaubt' | 'verborgen' | 'unbekannt'

/**
 * Die eine Sichtbarkeitsentscheidung der Shell — für Module.
 *
 * @param roles Rollen des angemeldeten Nutzers, `undefined` solange unbekannt.
 * @param module Das Manifest des Moduls.
 */
export function moduleAccess(
  roles: string[] | undefined,
  module: ModuleConfig,
): ModuleAccess {
  return roleAccess(roles, module.roles)
}

/**
 * Dieselbe Entscheidung für die Bereiche der Plattform selbst — Verwaltung und
 * Feedback —, die kein Manifest haben, aber denselben Ladezustand kennen müssen.
 *
 * @param roles Rollen des angemeldeten Nutzers, `undefined` solange unbekannt.
 * @param required Verlangte Rollen. Leer heißt „für alle Angemeldeten".
 */
export function roleAccess(
  roles: string[] | undefined,
  required: string[],
): ModuleAccess {
  if (roles === undefined) return 'unbekannt'
  return hasAnyRole(roles, required) ? 'erlaubt' : 'verborgen'
}
